import { inject, type InjectionKey } from 'vue';
import { getCurrentInstance, type ComponentInternalInstance } from "@vue/runtime-core";
import { isArrayOfType, isNil, isObject, isString, isSymbol, isUndefined } from "@mcwd/typescript-type-guards";


/**
 * An object representing a group of values to be returned by inject, with any string key as a friendly identifier.
 */
export type InjectionValuesRecord<TInjectionValues extends Record<string, any>> = TInjectionValues;

export type AnyInjectionKeysRecord<PropNames extends string = string, TInjectionKeys extends Record<PropNames, InjectionKey<any>> = Record<PropNames, InjectionKey<any>>> = TInjectionKeys; 

/**
 * An object representing a group of typed InjectionKeys to be used for provide and inject, with any string key as a friendly identifier.
 */
export type InjectionKeysRecord<TInjectionValues extends Record<string, any>> = {
  [key in Extract<keyof InjectionValuesRecord<TInjectionValues>, string>]: InjectionKey<
    TInjectionValues[key]
  >;
};

type KeysRecord<TKeys extends string> = {
  [key in TKeys]: symbol | key;
};

export function CreateInjectionKeysRecord<TInjectionKeysRecord extends InjectionKeysRecord<Record<string, any>>>(
  keysRecord: KeysRecord<string & keyof TInjectionKeysRecord>
): TInjectionKeysRecord {
  const keys = Object.keys(keysRecord) as (string & keyof TInjectionKeysRecord & keyof typeof keysRecord)[];
  for (const k of keys) {
    const keyValue = keysRecord[k];
    if (isString(keyValue)) {
      keysRecord[k] = Symbol(keyValue);
    }
  }
  return keysRecord as unknown as TInjectionKeysRecord;
}


type ComponentInternalInstanceWithProvidedKey<TKey extends symbol, TValue> = ComponentInternalInstance & { provides: Record<TKey, TValue> };
type SymbolType<TSymbol extends symbol> = ReturnType<TSymbol["valueOf"]>;
function checkCurrentInstanceProvidesKey<T>(vm: ComponentInternalInstance, key: (InjectionKey<T> & symbol) | symbol): vm is ComponentInternalInstanceWithProvidedKey<SymbolType<typeof key>, T> {
  if ("provides" in vm && isObject(vm.provides)) {
    const keySymbol = key.valueOf();
    if (keySymbol in vm.provides) {
      const value = (vm.provides as Partial<Record<PropertyKey, unknown>>)[keySymbol] as T | undefined;
      return (!isUndefined(value));
    }
  }
  return false;
}

type InjectFnSimpleParameters<T> =
  | readonly [ key: string ]
  | readonly [ key: InjectionKey<T> ]
  | readonly [ key: InjectionKey<T> | string ];

type InjectFnResult<T, TArgs extends InjectFnSimpleParameters<T>> =
  TArgs extends [key:  string]
    ? unknown | undefined
    :
  TArgs extends [key: InjectionKey<T>]
    ? T | undefined
    :
  TArgs extends [key: InjectionKey<T> | string]
    ? T | undefined
    : never

interface SimpleInjectFn<T, TArgs extends InjectFnSimpleParameters<T>>
{
  (...args: TArgs): InjectFnResult<T, typeof args>
}
function injectRequiredWithInjectFn<T, TArgs extends InjectFnSimpleParameters<T>> (injectFn: SimpleInjectFn<T, TArgs>, ...args: TArgs): T {
  const value = injectFn(...args) as T | undefined;
  if (isUndefined(value)){
    if (isSymbol(args[ 0 ])) {
      throw new Error(`injection value for key Symbol("${ args[ 0 ].description }") is undefined.`);
    }
    throw new Error(`injection value for key "${ args[0] }" is undefined.`);
  }
  else {
    return value;
  }
}


export function injectWithSelf<T>(key: InjectionKey<T>, defaultValue?: T | undefined): T | undefined {
  if (import.meta.env.SSR) {
    console.warn(new Error(`Current instance does not exist in SSR mode! Error encountered while attempting to inject Symbol("${key.description}")`));
    return inject<T>(key);
  }
  const vm = getCurrentInstance();
  if (isObject(vm)) {
    if (checkCurrentInstanceProvidesKey<T>(vm, key.valueOf())) {
      const res = vm.provides[ key.valueOf() ];
      if (res != null) {
        console.debug(`Inject value with current instance for key Symbol(${ key.description })`);
      }
      return res;
    }
    else {
      if (defaultValue != null) {
        console.debug(`Inject default value with current instance for key Symbol(${ key.description })`, defaultValue);
      }
      return defaultValue;
    }
  }
  else
  {
    console.warn(new Error(`Could not find current instance while attempting to inject Symbol("${key.description}")`));
  }
  if (!isUndefined(defaultValue)) {
    return inject<T>(key, defaultValue);
  }
  return inject<T>(key);
}

export function checkValuesAlreadyProvided(injectionKeys: AnyInjectionKeysRecord): boolean;
export function checkValuesAlreadyProvided<TInjectionKey extends InjectionKey<unknown>>(...injectionKeys: TInjectionKey[]): boolean;
export function checkValuesAlreadyProvided(...injectionKeys: ([InjectionKey<unknown>, ...InjectionKey<unknown>[]] | [AnyInjectionKeysRecord])): boolean {
  const obj: InjectionKey<unknown> | AnyInjectionKeysRecord = injectionKeys[0];
  if (isNil(obj)) {
    throw new Error("Expected at least one argument to checkValuesAlreadyProvided");
  }

  if (!isSymbol(obj)) {
    injectionKeys = Object.values(obj) as [InjectionKey<unknown>, ...InjectionKey<unknown>[]];
  }

  if (isArrayOfType(injectionKeys, isSymbol)) {
    return (injectionKeys as symbol[]).every((k) => {
      const defaultVal = "__ThisIsANULLINJECTIONVALUE___";
      const val = injectWithSelf<unknown>(k, defaultVal);
      return val !== defaultVal;
    });
  }
  else {
    throw new Error("Unexpected arguments supplied to checkValuesAlreadyProvided");
  }
}

export function injectRequiredWithSelf<T>(key: InjectionKey<T>): T {
  return injectRequiredWithInjectFn(injectWithSelf<T>, key);
}

export function injectRequired<T> (key: InjectionKey<T>): T
{
  return injectRequiredWithInjectFn(inject<T>, key);
}